"use client";

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "peptide-favorites";

export function useFavorites() {
	const [slugs, setSlugs] = useState<string[]>([]);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		queueMicrotask(() => {
			setMounted(true);
			try {
				const raw = localStorage.getItem(STORAGE_KEY);
				const parsed = raw ? (JSON.parse(raw) as string[]) : [];
				setSlugs(Array.isArray(parsed) ? parsed : []);
			} catch {
				setSlugs([]);
			}
		});
	}, []);

	const persist = useCallback((getNext: (prev: string[]) => string[]) => {
		setSlugs((prev) => {
			const next = getNext(prev);
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
			} catch {
				// Ignore quota / private mode
			}
			return next;
		});
	}, []);

	const add = useCallback(
		(slug: string) => {
			const s = slug.trim().toLowerCase();
			if (!s) return;
			persist((prev) => (prev.includes(s) ? prev : [...prev, s]));
		},
		[persist],
	);

	const remove = useCallback(
		(slug: string) => {
			const s = slug.trim().toLowerCase();
			persist((prev) => prev.filter((x: string) => x !== s));
		},
		[persist],
	);

	const toggle = useCallback(
		(slug: string) => {
			const s = slug.trim().toLowerCase();
			persist((prev) => (prev.includes(s) ? prev.filter((x: string) => x !== s) : [...prev, s]));
		},
		[persist],
	);

	const has = useCallback((slug: string) => slugs.includes(slug.trim().toLowerCase()), [slugs]);

	return { slugs, add, remove, toggle, has, mounted };
}
